import React, { useEffect, useRef, useState } from "react";
import Head from "next/head";
import Layout from "@/layout";
import styles from "./index.module.css";
import Header from "@/components/webHeader";
import dynamic from "next/dynamic";
import { useStoreActions, useStoreState } from "@/store";
import Script from "next/script";
import arrow from "@/assets/images/banner-arrow.png";
import { IResponseInitPPT } from "@/services/home/types";
import { cardList, cardList2 } from "@/constants/home";
import Footer from "@/layout/footer";
import { statTrack } from "@/utils/mtstat";
import { isMobile } from "@/utils";
import {
  getDesignHome,
  HOME_DESCRIPTION,
  HOME_KEYWORDS,
  TITLE,
} from "@/constants";
import { getQuery } from "@/utils/query";
import Module from "./components/module";
import CardModule from "./components/cardModule";
import CustomModule from "./components/customModule";
import Scene from "./components/scene";
import Marquee from "./components/marquee";
import ExperienceEntrance from "./components/experienceEntrance";
const DeepSeekBtn = dynamic(() => import("@/components/deepSeekBtn"), {
  ssr: false,
});
const PromotionComponent = dynamic(
  () => import("@/components/promotionModal"),
  {
    ssr: false,
  }
);
const OverseasAccessModal = dynamic(
  // @ts-expect-error
  () => import("@meitu/xdesign-widgets/dist/overseasAccessModal"),
  {
    ssr: false,
  }
);

interface IProps {
  isPersonal: boolean;
  initData: IResponseInitPPT["data"];
}

const Home: React.FC<IProps> = (props) => {
  const defaultPageSize = "16-20页";
  const { isPersonal, initData } = props;
  const isOpenDeepSeek = useStoreState((state) => state.isOpenDeepSeek);
  const setIsOpenDeepSeek = useStoreActions(
    (actions) => actions.setIsOpenDeepSeek
  );
  const videoRef = useRef<HTMLVideoElement>(null);
  const [placeholderPrompt, setPlaceholderPrompt] = useState<string>(
    initData.placeholder_prompt
  );
  const [keyword, setKeyword] = useState<string>("");
  const query = getQuery();

  const handleClickDeepSeek = (active: boolean) => {
    setIsOpenDeepSeek(active);
  };

  const keywordChangeHandle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value === keyword) return;
    setKeyword(value.trim());
    window.sessionStorage.setItem("pptKeyword", value.trim());
  };

  const handleJump = (word: string) => {
    // TODO 跳转workbench
    const pptUrl = `${getDesignHome()}/ppt/edit?from=ppt_landingpage`;
    const from = isMobile() ? "h5" : "";
    const hb_position = query.hb_position || "";
    const hb_operation = query.hb_operation || "";
    const channel = hb_position || hb_operation ? "app" : query.channel || "";
    const tool_url = query.tool_url || "";
    const indtool_source = query.indtool_source || "";
    window.location.href = `${pptUrl}&ppt_home_page=landing_page&keyword=${encodeURIComponent(
      word
    )}&pageSize=${encodeURIComponent(
      isMobile() ? "10-15页" : defaultPageSize
    )}${
      channel ? `&channel=${channel}` : ""
    }&from=${from}&indtool_source=${encodeURIComponent(
      indtool_source
    )}&tool_url=${encodeURIComponent(
      tool_url
    )}&hb_operation=${encodeURIComponent(
      hb_operation
    )}&hb_position=${encodeURIComponent(hb_position)}&is_deepseek=${
      isOpenDeepSeek ? "1" : "0"
    }`;
  };

  // 免费生成大纲
  const freeCreate = async () => {
    if (!keyword) {
      setKeyword(placeholderPrompt);
      setTimeout(() => {
        handleJump(placeholderPrompt);
      }, 0);
      return;
    }

    handleJump(keyword);
  };

  useEffect(() => {
    statTrack("ai_create_show", {
      ppt_home_page: "landing_page",
    });
  }, []);

  useEffect(() => {
    // 监听video的播放暂停
    const video = videoRef.current;
    if (video) {
      video.addEventListener("play", () => {
        statTrack("tools_click_ppt_home", {
          click_type: "video",
        });
      });
    }
    return () => {
      if (video) {
        video.removeEventListener("play", () => {});
      }
    };
  }, []);

  return (
    <Layout>
      <Head>
        <title>{TITLE}</title>
        <meta name="description" content={HOME_DESCRIPTION} />
        <meta name="keywords" content={HOME_KEYWORDS} />
      </Head>
      <Script
        src="https://public.static.meitudata.com/x-design/domain/mark-domain.js"
        async
      />
      {/* fix wxLogin */}
      <Script src="https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js" />
      <div className={styles.home}>
        <Header isPersonal={isPersonal} />
        <div className={styles.content}>
          <div className={styles.title}>
            <span className={styles.tag}>LivePPT</span>
            输入PPT主题
            <br />
            AI生成高质量PPT
          </div>

          {/* 输入框 */}
          <div className={styles.inputWrap}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              className={styles.arrow}
              src={arrow.src}
              alt="arrow"
              width={63}
            />
            <div className={styles.deepSeekBtn}>
              <DeepSeekBtn
                active={isOpenDeepSeek}
                onClick={handleClickDeepSeek}
              />
            </div>
            <div className={styles.inner}>
              <input
                className={styles.input}
                maxLength={300}
                onChange={keywordChangeHandle}
                type="text"
                value={keyword}
                onBlur={(e) => {
                  if (keyword) {
                    statTrack("tools_click_ppt_home", {
                      click_type: "inputbox",
                    });
                  }
                }}
                placeholder={initData.placeholder_text}
              />
              <button className={styles.btn} onClick={freeCreate}>
                免费生成大纲
              </button>
            </div>
          </div>

          {/* 视频 */}
          <div className={styles.video}>
            <video
              ref={videoRef}
              src="https://public.static.meitudata.com/xiuxiu-pc/x-design/ppt/video-new3.mp4"
              autoPlay
              muted
              loop
              playsInline
              controls
              x5-playsinline="true"
              webkit-playsinline="true"
              x5-video-orientation="portraint"
            />
          </div>

          {/* 动态模式全新推出，让PPT汇报更生动 */}
          <Module />

          {/* AI在线秒出PPT，多种生成方式更灵活 */}
          <CardModule
            cardList={cardList}
            title="AI在线秒出PPT，多种生成方式更灵活"
          />

          {/* 内容/模板均可自定义，生成更合心意 */}
          <CustomModule />

          {/* 文字/图片可AI编辑，智能设计更高效 */}
          <CardModule
            cardList={cardList2}
            title="文字/图片可AI编辑，智能设计更高效"
          />

          {/* 覆盖多场景 满足多人群PPT需求 */}
          <Scene />

          {/* 提供风格多样的高级感PPT模板 */}
          <Marquee />

          <ExperienceEntrance />
        </div>
        <OverseasAccessModal track={statTrack} />
        <PromotionComponent triggerPage={8192} />
      </div>
      <Footer />
    </Layout>
  );
};

export default Home;
